import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  IconButton,
  Paper,
  Chip,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  UploadFile as UploadIcon,
  TextFields as TextIcon,
  RadioButtonChecked as RadioIcon,
  CheckBox as CheckboxIcon,
  Draw as SignIcon,
  Description as FileReviewIcon,
} from '@mui/icons-material';
import { enhancedApi } from '../../api';

const ITEM_TYPES = [
  { value: 'document_upload', label: 'Document Upload', icon: <UploadIcon fontSize="small" /> },
  { value: 'text_answer', label: 'Text Answer', icon: <TextIcon fontSize="small" /> },
  { value: 'multiple_choice', label: 'Multiple Choice', icon: <RadioIcon fontSize="small" /> },
  { value: 'checkbox', label: 'Checkbox', icon: <CheckboxIcon fontSize="small" /> },
  { value: 'esignature', label: 'E-Signature', icon: <SignIcon fontSize="small" /> },
  { value: 'file_review', label: 'File Review', icon: <FileReviewIcon fontSize="small" /> },
];

function normalizeChoices(item) {
  const ch = item.choices;
  if (ch == null) return { mode: 'list', list: [], extra: {} };
  if (Array.isArray(ch)) return { mode: 'list', list: ch, extra: {} };
  if (typeof ch === 'object') {
    return {
      mode: 'object',
      list: Array.isArray(ch.options) ? ch.options : [],
      extra: ch,
    };
  }
  return { mode: 'list', list: [], extra: {} };
}

function serializeChoices(item) {
  const { item_type: t } = item;
  if (t === 'multiple_choice' || t === 'checkbox') {
    return item.choicesList?.length ? item.choicesList : [];
  }
  if (t === 'text_answer') {
    const ph = (item.placeholder || '').trim();
    return ph ? { placeholder: ph } : null;
  }
  if (t === 'esignature') {
    return item.esignature_file_id ? { esignature_file_id: item.esignature_file_id } : null;
  }
  if (t === 'file_review') {
    return item.review_file_id ? { review_file_id: item.review_file_id } : null;
  }
  return null;
}

function SortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };
  return (
    <Box ref={setNodeRef} style={style}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <IconButton size="small" {...attributes} {...listeners} sx={{ mt: 0.5, cursor: 'grab' }}>
          <DragIcon sx={{ color: 'text.disabled' }} />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      </Box>
    </Box>
  );
}

function ItemEditor({ item, onChange, onDelete, clientId, fileOptions, fileLoading }) {
  const [choiceInput, setChoiceInput] = useState('');
  const nc = normalizeChoices(item);
  const choicesList = item.choicesList ?? nc.list;

  const addChoice = () => {
    const val = choiceInput.trim();
    if (!val) return;
    onChange({ ...item, choicesList: [...choicesList, val] });
    setChoiceInput('');
  };

  const removeChoice = (idx) => {
    const next = choicesList.filter((_, i) => i !== idx);
    onChange({ ...item, choicesList: next });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={item.item_type}
            onChange={(e) => {
              const nextType = e.target.value;
              onChange({
                ...item,
                item_type: nextType,
                choicesList: ['multiple_choice', 'checkbox'].includes(nextType) ? (item.choicesList || []) : undefined,
                placeholder: nextType === 'text_answer' ? item.placeholder : undefined,
                esignature_file_id: nextType === 'esignature' ? item.esignature_file_id : undefined,
                review_file_id: nextType === 'file_review' ? item.review_file_id : undefined,
              });
            }}
          >
            {ITEM_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {t.icon} {t.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Label"
          value={item.label}
          onChange={(e) => onChange({ ...item, label: e.target.value })}
          sx={{ flex: 1, minWidth: 160 }}
          required
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={item.is_required}
              onChange={(e) => onChange({ ...item, is_required: e.target.checked })}
            />
          }
          label={<Typography variant="caption">Required</Typography>}
          sx={{ flexShrink: 0 }}
        />
        <Tooltip title="Remove item">
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <TextField
        size="small"
        fullWidth
        label="Instructions (optional)"
        value={item.description}
        onChange={(e) => onChange({ ...item, description: e.target.value })}
        sx={{ mb: 1 }}
      />

      {item.item_type === 'text_answer' && (
        <TextField
          size="small"
          fullWidth
          label="Placeholder (optional)"
          value={item.placeholder || ''}
          onChange={(e) => onChange({ ...item, placeholder: e.target.value })}
          sx={{ mb: 1 }}
        />
      )}

      {(item.item_type === 'multiple_choice' || item.item_type === 'checkbox') && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Options
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
            {choicesList.map((c, i) => (
              <Chip key={i} label={c} size="small" onDelete={() => removeChoice(i)} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Add option…"
              value={choiceInput}
              onChange={(e) => setChoiceInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addChoice();
                }
              }}
              sx={{ flex: 1 }}
            />
            <Button size="small" onClick={addChoice} variant="outlined">
              Add
            </Button>
          </Box>
        </Box>
      )}

      {item.item_type === 'esignature' && clientId && (
        <Autocomplete
          size="small"
          options={fileOptions}
          loading={fileLoading}
          getOptionLabel={(o) => o.file_name || o.name || `File ${o.id}`}
          value={fileOptions.find((f) => f.id === item.esignature_file_id) || null}
          onChange={(_, v) => onChange({ ...item, esignature_file_id: v?.id || null })}
          renderInput={(params) => (
            <TextField {...params} label="Document to sign" placeholder="Select client file" />
          )}
          sx={{ mt: 1 }}
        />
      )}

      {item.item_type === 'esignature' && !clientId && (
        <Typography variant="caption" color="warning.main">
          Select a client in step 1 to pick a document for e-signature.
        </Typography>
      )}

      {item.item_type === 'file_review' && clientId && (
        <Autocomplete
          size="small"
          options={fileOptions}
          loading={fileLoading}
          getOptionLabel={(o) => o.file_name || o.name || `File ${o.id}`}
          value={fileOptions.find((f) => f.id === item.review_file_id) || null}
          onChange={(_, v) => onChange({ ...item, review_file_id: v?.id || null })}
          renderInput={(params) => (
            <TextField {...params} label="Document for client to review" placeholder="Select client file" />
          )}
          sx={{ mt: 1 }}
        />
      )}

      {item.item_type === 'file_review' && !clientId && (
        <Typography variant="caption" color="warning.main">
          Select a client in step 1 to attach a review document.
        </Typography>
      )}
    </Paper>
  );
}

export default function RequestItemBuilder({ items, onChange, clientId }) {
  const [fileOptions, setFileOptions] = useState([]);
  const [fileLoading, setFileLoading] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setFileOptions([]);
      return;
    }
    let cancelled = false;
    setFileLoading(true);
    enhancedApi
      .getClientFiles(clientId)
      .then((res) => {
        if (cancelled) return;
        const raw = res.data?.results ?? res.data?.files ?? res.data;
        setFileOptions(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        if (!cancelled) setFileOptions([]);
      })
      .finally(() => {
        if (!cancelled) setFileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const ids = useMemo(() => items.map((it) => String(it._key || it.id)), [items]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(items, oldIndex, newIndex).map((row, i) => ({ ...row, order: i })));
  };

  const addItem = () => {
    onChange([
      ...items,
      {
        _key: `k_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        item_type: 'document_upload',
        label: '',
        description: '',
        is_required: true,
        order: items.length,
        choicesList: [],
      },
    ]);
  };

  const updateItem = (idx, updated) => {
    const next = [...items];
    next[idx] = { ...updated, order: idx };
    onChange(next);
  };

  const deleteItem = (idx) => {
    const next = items.filter((_, i) => i !== idx).map((item, i) => ({ ...item, order: i }));
    onChange(next);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Drag items to reorder. Each item needs a label before sending.
        </Typography>
      </Box>

      {items.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center', py: 2 }}>
          No items yet. Add at least one thing to request from your client.
        </Typography>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {items.map((item, idx) => (
            <SortableRow key={item._key || item.id || idx} id={String(item._key || item.id)}>
              <ItemEditor
                item={item}
                onChange={(u) => updateItem(idx, u)}
                onDelete={() => deleteItem(idx)}
                clientId={clientId}
                fileOptions={fileOptions}
                fileLoading={fileLoading}
              />
            </SortableRow>
          ))}
        </SortableContext>
      </DndContext>

      <Button startIcon={<AddIcon />} onClick={addItem} variant="outlined" size="small" fullWidth>
        Add Item
      </Button>
    </Box>
  );
}

export function itemsToPayload(items) {
  return items.map((item, idx) => ({
    item_type: item.item_type,
    label: (item.label || '').trim(),
    description: item.description || '',
    is_required: Boolean(item.is_required),
    order: idx,
    choices: serializeChoices(item),
  }));
}

export function templateItemsToFormItems(rawItems) {
  if (!Array.isArray(rawItems)) return [];
  return rawItems.map((row, i) => {
    const item = typeof row === 'object' && row ? row : {};
    const t = item.item_type || 'document_upload';
    const base = {
      _key: `tpl_${Date.now()}_${i}`,
      item_type: t,
      label: item.label || '',
      description: item.description || '',
      is_required: item.is_required !== false,
      order: i,
    };
    if (t === 'multiple_choice' || t === 'checkbox') {
      base.choicesList = Array.isArray(item.choices) ? item.choices : [];
    }
    if (t === 'text_answer' && item.choices && typeof item.choices === 'object' && item.choices.placeholder) {
      base.placeholder = item.choices.placeholder;
    }
    if (t === 'esignature' && item.choices?.esignature_file_id) {
      base.esignature_file_id = item.choices.esignature_file_id;
    }
    if (t === 'file_review' && item.choices?.review_file_id) {
      base.review_file_id = item.choices.review_file_id;
    }
    return base;
  });
}
